/**
 * A tiny billboarded health bar that floats above a character. Background +
 * fill quads; the fill scales with the health fraction and shifts color from
 * green to red. Uses unlit (emissive) materials so it reads at any angle.
 */
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder.js";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial.js";
import { Color3 } from "@babylonjs/core/Maths/math.color.js";
import { Mesh } from "@babylonjs/core/Meshes/mesh.js";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode.js";
import type { Scene } from "@babylonjs/core/scene.js";

export interface HealthBar {
  set(frac: number): void;
}

const WIDTH = 1.2;

export const createHealthBar = (
  scene: Scene,
  parent: TransformNode,
  y = 2.3,
): HealthBar => {
  const bg = MeshBuilder.CreatePlane("hp-bg", { width: WIDTH, height: 0.16 }, scene);
  const fill = MeshBuilder.CreatePlane("hp-fill", { width: WIDTH, height: 0.16 }, scene);
  for (const m of [bg, fill]) {
    m.parent = parent;
    m.position.y = y;
    m.billboardMode = Mesh.BILLBOARDMODE_ALL;
    m.isPickable = false;
  }
  fill.position.z = -0.01; // sit just in front of the background

  const bgMat = new StandardMaterial("hpBgMat", scene);
  bgMat.disableLighting = true;
  bgMat.emissiveColor = new Color3(0.08, 0.09, 0.12);
  bg.material = bgMat;

  const fillMat = new StandardMaterial("hpFillMat", scene);
  fillMat.disableLighting = true;
  fillMat.emissiveColor = new Color3(0.3, 0.9, 0.4);
  fill.material = fillMat;

  return {
    set(frac: number): void {
      const f = Math.max(0, Math.min(1, frac));
      fill.scaling.x = f;
      fillMat.emissiveColor = new Color3(1 - f, 0.3 + 0.6 * f, 0.25);
    },
  };
};
